import { useCallback, useEffect, useRef, useState } from 'react';
import { getMembers } from '@/api/member';
import type { GetMemberListReq, Member, MemberListStatus } from '@shared/api.interface';

const PAGE_SIZE = 20;
type Query = { search: string; page: number; statuses: MemberListStatus[]; quotaMode: 'all' | 'monthly' | 'total'; batchId?: number };

export function useMemberList(enabled: boolean) {
  const [query, setQuery] = useState<Query>({ search: '', page: 1, statuses: [], quotaMode: 'all' });
  const queryRef = useRef(query);
  queryRef.current = query;
  const [memberSearch, setSearch] = useState('');
  const searchRef = useRef('');
  const composing = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const sequence = useRef(0);
  const [members, setMembers] = useState<Member[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoadingMembers, setLoading] = useState(false);
  const [memberError, setError] = useState('');

  const invalidate = useCallback(() => {
    sequence.current += 1;
    setError('');
    setLoading(true);
  }, []);

  const fetchMembers = useCallback(async () => {
    if (!enabled || composing.current) return;
    const request = ++sequence.current;
    const current = queryRef.current;
    setLoading(true);
    setError('');
    try {
      const params: GetMemberListReq = {
        search: current.search, page: current.page, pageSize: PAGE_SIZE,
        statuses: current.statuses.length ? current.statuses.join(',') : undefined,
        quotaMode: current.quotaMode === 'all' ? undefined : current.quotaMode,
        batchId: current.batchId,
      };
      const result = await getMembers(params);
      if (sequence.current !== request) return;
      const lastPage = Math.max(1, Math.ceil(result.total / PAGE_SIZE));
      if (current.page > lastPage) {
        setQuery(previous => ({ ...previous, page: lastPage }));
        return;
      }
      setMembers(result.items);
      setTotal(result.total);
      setLoading(false);
    } catch {
      if (sequence.current !== request) return;
      setError('获取会员列表失败，请重试');
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    void fetchMembers();
    return () => { sequence.current += 1; };
  }, [query, fetchMembers]);

  useEffect(() => () => { clearTimeout(timer.current); }, []);

  const scheduleSearch = useCallback((value: string) => {
    clearTimeout(timer.current);
    if (composing.current) return;
    timer.current = setTimeout(() => {
      setQuery(previous => ({ ...previous, search: value.trim(), page: 1 }));
    }, 300);
  }, []);

  const setMemberSearch = (value: string) => {
    searchRef.current = value;
    setSearch(value);
    invalidate();
    scheduleSearch(value);
  };
  const changeConditions = (change: Partial<Query>) => {
    clearTimeout(timer.current);
    const search = searchRef.current.trim();
    invalidate();
    setQuery(previous => ({ ...previous, ...change, search, page: 1 }));
  };
  const setMemberQuotaFilter = (quotaMode: Query['quotaMode']) => changeConditions({ quotaMode });
  const setMemberBatchFilter = (batchId: number | undefined) => changeConditions({ batchId });
  const toggleMemberStatusFilter = (status: MemberListStatus) => {
    const statuses = queryRef.current.statuses;
    changeConditions({ statuses: statuses.includes(status) ? statuses.filter(item => item !== status) : [...statuses, status] });
  };
  const setMemberPage = (page: number) => {
    invalidate();
    setQuery(previous => ({ ...previous, page }));
  };

  return {
    members, memberSearch, setMemberSearch, isLoadingMembers, memberError, fetchMembers,
    memberTotal: total, memberPage: query.page, memberTotalPages: Math.max(1, Math.ceil(total / PAGE_SIZE)),
    setMemberPage, memberQuotaFilter: query.quotaMode, setMemberQuotaFilter,
    memberStatusFilters: query.statuses, toggleMemberStatusFilter,
    memberBatchFilter: query.batchId, setMemberBatchFilter,
    onMemberCompositionStart: () => {
      composing.current = true;
      clearTimeout(timer.current);
      invalidate();
    },
    onMemberCompositionEnd: (value: string) => {
      composing.current = false;
      searchRef.current = value;
      setSearch(value);
      invalidate();
      scheduleSearch(value);
    },
  };
}
